import { useMemo, useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
	DndContext,
	closestCenter,
	rectIntersection,
	PointerSensor,
	TouchSensor,
	KeyboardSensor,
	useSensor,
	useSensors,
	DragEndEvent,
	DragOverlay,
	DragStartEvent,
	DragCancelEvent,
	DragOverEvent,
} from '@dnd-kit/core'
import { useProgramContext } from '@/context/ProgramContext'
import { apiClient } from '@/lib/api'
import { RoadmapColumn } from '@/components/roadmap/RoadmapColumn'
import { RoadmapFeatureCard } from '@/components/roadmap/RoadmapFeatureCard'
import {
	Loader2,
	BookOpen,
	Settings,
	Plus,
	Sparkles,
	AlertCircle,
	RefreshCw,
	ExternalLink,
	Map,
	ChevronLeft,
	ChevronRight,
	Globe,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import type { FeatureRequest } from '@/lib/services/feedback'
import type { Column } from '@/components/settings/ColumnManager'
import { CreateFeatureModal } from '@/components/feature-requests/CreateFeatureModal'
import { FeatureRequestDetailView } from '@/components/feature-requests/FeatureRequestDetailView'
import { Card, CardContent } from '@/components/ui/card'
import { useTranslation } from '@/hooks/useTranslation'

interface RoadmapData {
	id: string
	name: string
	slug: string
	is_public: boolean
	roadmap_columns: Column[]
	features: FeatureRequest[]
	logo_url?: string | null
}

// Loading skeleton component
const RoadmapSkeleton = () => (
	<div className="flex h-full flex-col p-4 md:p-8">
		<div className="flex-shrink-0 mb-8 flex justify-between items-center">
			<div className="space-y-2">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-4 w-96" />
			</div>
			<Skeleton className="h-10 w-32" />
		</div>
		<div className="flex flex-1 gap-6 overflow-x-auto pb-4">
			{Array.from({ length: 4 }).map((_, i) => (
				<div key={i} className="flex-shrink-0 w-80">
					<div className="bg-muted/30 rounded-xl p-4 h-full">
						<Skeleton className="h-6 w-32 mb-4" />
						<div className="space-y-3">
							{Array.from({ length: 3 }).map((_, j) => (
								<Skeleton key={j} className="h-24 w-full rounded-lg" />
							))}
						</div>
					</div>
				</div>
			))}
		</div>
	</div>
)

// Enhanced empty state component
const EmptyRoadmapState = ({ currentProgram }: { currentProgram: any }) => {
	const { t } = useTranslation()
	return (
	<motion.div
		initial={{ opacity: 0, scale: 0.95 }}
		animate={{ opacity: 1, scale: 1 }}
		transition={{ duration: 0.3 }}
		className="flex h-full flex-col items-center justify-center gap-6 p-8 text-center mx-auto">
		<div className="relative">
			<div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl" />
			<div className="relative bg-gradient-to-br from-blue-500/10 to-purple-500/10 p-6 rounded-2xl border border-border/50">
				<Map className="h-12 w-12 text-muted-foreground mx-auto" />
			</div>
		</div>

		<div className="space-y-3">
			<h2 className="text-2xl font-bold tracking-tight">
				{t('roadmapPage.createYourFirstRoadmap')}
			</h2>
			<p className="text-muted-foreground leading-relaxed">
				{t('roadmapPage.transformYourProgram', { programName: currentProgram?.name })}
				<br />
				{t('roadmapPage.organizeFeatures')}
			</p>
		</div>

		<div className="flex flex-col sm:flex-row gap-3 mt-2">
			<Button asChild size="lg" className="bg-gradient-to-r ">
				<Link to="/roadmap-settings">
					<Sparkles className="mr-2 h-4 w-4" />
					{t('roadmapPage.configureRoadmap')}
				</Link>
			</Button>
		</div>
	</motion.div>
	)
}

// Error state component
const ErrorState = ({
	error,
	onRetry,
}: {
	error: Error
	onRetry: () => void
}) => {
	const { t } = useTranslation()
	return (
	<motion.div
		initial={{ opacity: 0, y: 20 }}
		animate={{ opacity: 1, y: 0 }}
		className="flex h-full flex-col items-center justify-center gap-6 p-8 text-center max-w-md mx-auto">
		<Alert variant="destructive" className="border-red-200">
			<AlertCircle className="h-4 w-4" />
			<AlertDescription className="text-sm">
				{error.message || t('roadmapPage.failedToLoadRoadmap')}
			</AlertDescription>
		</Alert>

		<Button onClick={onRetry} variant="outline" size="lg">
			<RefreshCw className="mr-2 h-4 w-4" />
			{t('roadmapPage.tryAgain')}
		</Button>
	</motion.div>
	)
}

export default function RoadmapPage() {
	const { t } = useTranslation()
	const { currentProgram, programId, currentProgramAccessLevel } = useProgramContext()
	const queryClient = useQueryClient()
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
	const [newFeatureStatus, setNewFeatureStatus] = useState<string | null>(null)
	const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(
		null
	)
	const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
	const [draggedItem, setDraggedItem] = useState<FeatureRequest | null>(null)
	const [isDragging, setIsDragging] = useState(false)
	const [draggedOverColumnId, setDraggedOverColumnId] = useState<string | null>(null)
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	const {
		data: roadmapData,
		isLoading,
		error,
		refetch,
	} = useQuery<RoadmapData, Error>({
		queryKey: ['roadmapDataWithFeatures', programId],
		queryFn: () => apiClient(`/api/roadmaps?programId=${programId}`),
		enabled: !!programId,
		staleTime: 30000, // 30 seconds
		refetchOnWindowFocus: false,
	})

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 200,
				tolerance: 5,
			},
		}),
		useSensor(KeyboardSensor)
	)

	const updateFeatureStatusMutation = useMutation({
		mutationFn: ({
			featureId,
			newStatus,
		}: {
			featureId: string
			newStatus: string
		}) =>
			apiClient(`/api/features/protected/${featureId}`, {
				method: 'PUT',
				data: { status: newStatus },
			}),
		onMutate: async ({
			featureId,
			newStatus,
		}: {
			featureId: string
			newStatus: string
		}) => {
			await queryClient.cancelQueries({
				queryKey: ['roadmapDataWithFeatures', programId],
			})

			const previousRoadmapData = queryClient.getQueryData<RoadmapData>([
				'roadmapDataWithFeatures',
				programId,
			])

			queryClient.setQueryData<RoadmapData>(
				['roadmapDataWithFeatures', programId],
				(oldData) => {
					if (!oldData) return oldData

					return {
						...oldData,
						features: oldData.features.map((f) =>
							f.id === featureId ? { ...f, status: newStatus } : f
						),
					}
				}
			)

			return { previousRoadmapData }
		},
		onSuccess: () => {
			// Invalidate dashboard roadmap data
			queryClient.invalidateQueries({
				queryKey: ['roadmapDataWithFeatures', programId],
			})
			// Also invalidate public roadmap queries since feature status affects public roadmap
			queryClient.invalidateQueries({
				queryKey: ['publicRoadmap'],
			})
		},
		onError: (err, variables, context) => {
			if (context?.previousRoadmapData) {
				queryClient.setQueryData(
					['roadmapDataWithFeatures', programId],
					context.previousRoadmapData
				)
			}
			queryClient.invalidateQueries({
				queryKey: ['roadmapDataWithFeatures', programId],
			})
			// Also invalidate public roadmap queries since feature status affects public roadmap
			queryClient.invalidateQueries({
				queryKey: ['publicRoadmap'],
			})
		},
	})

	const createFeatureMutation = useMutation({
		mutationFn: (newFeature: {
			title: string
			description: string
			status: string
			category_id?: string | null
			tag_ids?: string[]
		}) => {
			if (!programId) throw new Error('Program ID is required')
			return apiClient('/api/features/protected', {
				method: 'POST',
				data: {
					programId,
					title: newFeature.title,
					description: newFeature.description,
					status: newFeature.status,
					is_publicly_visible: true,
					category_id: newFeature.category_id,
					tag_ids: newFeature.tag_ids,
				},
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['roadmapDataWithFeatures', programId],
			})
			// Also invalidate public roadmap queries since new features affect public roadmap
			queryClient.invalidateQueries({
				queryKey: ['publicRoadmap'],
			})
		},
		onError: (error: any) => {
			console.error('Error creating feature:', error)
		},
	})

	const handleDragStart = (event: DragStartEvent) => {
		// Prevent dragging for read-only users
		if (currentProgramAccessLevel === 'read') {
			return
		}
		
		const draggedFeature = roadmapData?.features.find(
			(f) => f.id === event.active.id
		)
		setDraggedItemId(event.active.id as string)
		setDraggedItem(draggedFeature || null)
		setIsDragging(true)
		setDraggedOverColumnId(null)
	}

	const handleDragOver = (event: DragOverEvent) => {
		const { over } = event
		
		if (!over) {
			setDraggedOverColumnId(null)
			return
		}

		let targetColumnId: string | null = null
		
		// Check if we're over a column directly
		const targetColumn = roadmapData?.roadmap_columns.find((c) => c.id === over.id)
		if (targetColumn) {
			targetColumnId = targetColumn.id
		} else {
			// Check if we're over a feature, and find its column
			const targetFeature = roadmapData?.features.find((f) => f.id === over.id)
			if (targetFeature) {
				const featureColumn = roadmapData?.roadmap_columns.find(
					(c) => c.mapped_status === targetFeature.status
				)
				if (featureColumn) {
					targetColumnId = featureColumn.id
				}
			}
		}
		
		setDraggedOverColumnId(targetColumnId)
	}

	const handleDragCancel = () => {
		setDraggedItemId(null)
		setDraggedItem(null)
		setIsDragging(false)
		setDraggedOverColumnId(null)
	}

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event

		setDraggedItemId(null)
		setDraggedItem(null)
		setIsDragging(false)
		setDraggedOverColumnId(null)

		// Prevent status changes for read-only users
		if (currentProgramAccessLevel === 'read') {
			return
		}

		if (!over) {
			return
		}

		const featureId = active.id as string
		let targetColumn: Column | undefined

		targetColumn = roadmapData?.roadmap_columns.find((c) => c.id === over.id)

		if (!targetColumn) {
			const targetFeature = roadmapData?.features.find((f) => f.id === over.id)
			if (targetFeature) {
				targetColumn = roadmapData?.roadmap_columns.find(
					(c) => c.mapped_status === targetFeature.status
				)
			}
		}

		const feature = roadmapData?.features.find((f) => f.id === featureId)

		if (
			feature &&
			targetColumn &&
			feature.status !== targetColumn.mapped_status
		) {
			updateFeatureStatusMutation.mutate({
				featureId,
				newStatus: targetColumn.mapped_status,
			})
		}
	}

	const handleAddFeature = (status: string) => {
		setNewFeatureStatus(status)
		setIsCreateModalOpen(true)
	}

	const handleCreateFeatureSubmit = async (data: {
		title: string
		description: string
		category_id?: string | null
		tag_ids?: string[]
	}) => {
		if (newFeatureStatus) {
			await createFeatureMutation.mutateAsync({
				title: data.title,
				description: data.description,
				status: newFeatureStatus,
				category_id: data.category_id,
				tag_ids: data.tag_ids,
			})
		}
	}

	const handleViewFeature = (featureId: string) => {
		setSelectedFeatureId(featureId)
	}

	const columns = useMemo(() => {
		return (
			roadmapData?.roadmap_columns?.sort(
				(a, b) => a.order_index - b.order_index
			) || []
		)
	}, [roadmapData])

	const featuresByStatus = useMemo(() => {
		const grouped: { [status: string]: FeatureRequest[] } = {}
		roadmapData?.features?.forEach((feature) => {
			const status = feature.status || 'New'
			if (!grouped[status]) {
				grouped[status] = []
			}
			grouped[status].push(feature)
		})
		return grouped
	}, [roadmapData])

	const totalFeatures = roadmapData?.features?.length || 0

	const scrollLeft = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: -320, // Width of one column plus gap
				behavior: 'smooth',
			})
		}
	}

	const scrollRight = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: 320, // Width of one column plus gap
				behavior: 'smooth',
			})
		}
	}

	if (isLoading) {
		return <RoadmapSkeleton />
	}

	if (error) {
		return <ErrorState error={error} onRetry={() => refetch()} />
	}

	if (!roadmapData?.id) {
		return <EmptyRoadmapState currentProgram={currentProgram} />
	}

	return (
		<div className="container max-w-7xl mx-auto py-6 space-y-6 overflow-hidden ">
			{/* Enhanced Header */}
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4 }}
				className="w-full bg-gradient-to-r from-background via-background to-background/95 ">
				<div className="flex flex-col sm:flex-row sm:items-center justify-between mr-6">
					<div className="space-y-2">
						<div className="flex items-center gap-3">
							
							<h1 className="text-2xl md:text-3xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
								{roadmapData.name} roadmap
							</h1>
						</div>
						<p className="text-muted-foreground text-sm md:text-base">
							{t('roadmapPage.dragAndDropDescription')}
						</p>
					</div>

					<div className="flex items-center gap-3">
						{roadmapData?.is_public && roadmapData?.slug && (
							<Button
								variant="outline"
								size="sm"
								asChild
								className="bg-gradient-to-r from-primary/10 to-primary/20 hover:from-primary/20 hover:to-primary/30 border-primary/20 text-primary/80 hover:text-primary">
								<a
									href={`${import.meta.env.PROD ? 'https' : 'http'}://${
										roadmapData.slug
									}.${
										import.meta.env.PROD ? 'feedbask.com' : 'localhost:8080'
									}`}
									target="_blank"
									rel="noopener noreferrer">
									<ExternalLink className="mr-2 h-4 w-4" />
									{t('roadmapPage.viewPublicRoadmap')}
								</a>
							</Button>
						)}

						<Button variant="outline" asChild>
							<Link to="/roadmap-settings">
								<Settings className="h-4 w-4" />
							</Link>
						</Button>
					</div>
				</div>
			</motion.div>

			<div className="flex items-center gap-2 justify-end mr-6">
				<Tooltip delayDuration={0}>
					<TooltipTrigger asChild>
						<Button
							variant="outline"
							size="sm"
							onClick={scrollLeft}
							className="h-8 w-8 p-0 bg-background/80 backdrop-blur-sm hover:bg-background hover:text-primary hover:border-primary">
							<ChevronLeft className="h-4 w-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="bottom">
						<p>{t('roadmapPage.scrollLeft')}</p>
					</TooltipContent>
				</Tooltip>
				<Tooltip delayDuration={0}>
					<TooltipTrigger asChild>
						<Button
							variant="outline"
							size="sm"
							onClick={scrollRight}
							className="h-8 w-8 p-0 bg-background/80 backdrop-blur-sm hover:bg-background hover:text-primary hover:border-primary">
							<ChevronRight className="h-4 w-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="bottom">
						<p>{t('roadmapPage.scrollRight')}</p>
					</TooltipContent>
				</Tooltip>
			</div>

			{/*  Roadmap Board */}
			<div className="h-full mr-6">
				{/* Scroll Navigation Buttons */}

				<DndContext
					sensors={sensors}
					collisionDetection={rectIntersection}
					onDragStart={handleDragStart}
					onDragOver={handleDragOver}
					onDragEnd={handleDragEnd}
					onDragCancel={handleDragCancel}>
					<LayoutGroup>
						<div
							ref={scrollContainerRef}
							className={cn(
								'flex flex-1 gap-4 md:gap-6 overflow-x-auto scrollbar-hide',
								isDragging && 'cursor-grabbing'
							)}>
							<AnimatePresence mode="popLayout">
								{columns.map((column, index) => {
									const columnFeatures =
										featuresByStatus[column.mapped_status] || []
									return (
										<motion.div
											key={column.id}
											layout
											initial={{ opacity: 0, x: 20 }}
											animate={{ opacity: 1, x: 0 }}
											exit={{ opacity: 0, x: -20 }}
											transition={{
												duration: 0.3,
												delay: index * 0.1,
												layout: { duration: 0.2 },
											}}
											className="flex-shrink-0">
											<RoadmapColumn
												column={column}
												features={columnFeatures}
												onAddFeature={handleAddFeature}
												onViewFeature={handleViewFeature}
												isDraggedOver={draggedOverColumnId === column.id}
												isReadOnly={currentProgramAccessLevel === 'read'}
											/>
										</motion.div>
									)
								})}
							</AnimatePresence>
						</div>
					</LayoutGroup>

					{/* Enhanced Drag Overlay */}
					<DragOverlay
						dropAnimation={null}>
						{draggedItem ? (
							<motion.div
								initial={{ scale: 1, rotate: 0 }}
								animate={{ scale: 1.05, rotate: 2 }}
								exit={{ scale: 1, rotate: 0 }}
								transition={{ duration: 0.2 }}
								className="w-72 opacity-95 cursor-grabbing shadow-2xl ring-2 ring-primary/20"
								style={{
									filter: 'drop-shadow(0 25px 25px rgb(0 0 0 / 0.15))',
								}}>
								<RoadmapFeatureCard
									feature={draggedItem}
									onViewDetails={() => {}}
								/>
							</motion.div>
						) : null}
					</DragOverlay>
				</DndContext>
			</div>

			{/* Modals */}
			<AnimatePresence>
				{newFeatureStatus && (
					<CreateFeatureModal
						isOpen={isCreateModalOpen}
						onClose={() => setIsCreateModalOpen(false)}
						onSubmit={handleCreateFeatureSubmit}
						initialStatus={newFeatureStatus}
					/>
				)}

				{selectedFeatureId && (
					<FeatureRequestDetailView
						featureId={selectedFeatureId}
						onClose={() => setSelectedFeatureId(null)}
						onUpdate={() =>
							queryClient.invalidateQueries({
								queryKey: ['roadmapDataWithFeatures', programId],
							})
						}
					/>
				)}
			</AnimatePresence>
		</div>
	)
}
